import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "./appointmentCard.css";

/**
 * Expects parent to compute and pass absolute screen coords:
 * position = { x: number, y: number }
 */
const AppointmentCard = ({ appointment, onClose, position }) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [coords, setCoords] = useState({ top: position.y, left: position.x });

  // Ensure card aligns with slot edges when it would clip
  useLayoutEffect(() => {
    const recalc = () => {
      const el = cardRef.current;
      if (!el) return;

      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const h = el.offsetHeight;
      const w = el.offsetWidth;

      let { x: left, y: top } = position;

      // If would clip at bottom, align card's bottom edge with slot's bottom edge
      if (top + h > vh) {
        const slotBottom = position.y + h/2; // Assuming the original y was centered
        top = slotBottom - h;
      }

      // If would clip at top, align card's top edge with slot's top edge
      if (top < 0) {
        const slotTop = position.y - h/2; // Assuming the original y was centered
        top = Math.max(0, slotTop);
      }

      // If would clip at right, move to left of slot
      if (left + w > vw) {
        left = Math.max(0, left - w - 24); // 24px offset from slot
      }

      setCoords({ top, left });
    };

    recalc();
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, { passive: true });

    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc);
    };
  }, [position]);

  const handleNavigateToInbox = () => {
    navigate("/attorney/inbox");
    onClose();
  };

  const handleOpenCase = () => {
    if (appointment?.id) {
      navigate(`/attorney/case/${appointment.id}`);
      onClose();
    }
  };

  const formatTime = (date) => moment(date).format("h:mm A");
  const formatDate = (date) => moment(date).format("MMM D, YYYY");

  const getDuration = () => {
    const start = moment(appointment.start);
    const end = moment(appointment.end);
    const duration = moment.duration(end.diff(start));
    return Math.round(duration.asMinutes());
  };

  return (
    <div
      ref={cardRef}
      className="appointment-card"
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        zIndex: 1002,
        maxHeight: "calc(100vh - 32px)",
        overflowY: "auto",
      }}
    >
      <div className="appointment-card-header">
        <div className="appointment-card-title">
          <div className="appointment-icon">📅</div>
          <div className="appointment-info">
            <h3>Appointment</h3>
            <p className="appointment-card-time">
              {formatTime(appointment.start)} - {formatTime(appointment.end)}
            </p>
            <p className="appointment-date">{formatDate(appointment.start)}</p>
            <p className="appointment-duration">{getDuration()} minutes</p>
          </div>
        </div>
        <button className="close-btn" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="appointment-card-content">
        <div className="appointment-details">
          <div className="detail-row">
            <span className="detail-label">Client:</span>
            <span className="detail-value">
              {appointment.client?.first_name && appointment.client?.last_name
                ? `${appointment.client.first_name} ${appointment.client.last_name}`
                : "Client Name Not Available"}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Email:</span>
            <span className="detail-value">
              {appointment.client?.email || "Email Not Available"}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Phone:</span>
            <span className="detail-value">
              {appointment.client?.phone || "Phone Not Available"}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Type:</span>
            <span className="detail-value">
              {appointment.appointmentType?.appointment_name || "Consultation"}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Status:</span>
            <span
              className={`detail-value status-${appointment.status || "confirmed"}`}
            >
              {appointment.status || "confirmed"}
            </span>
          </div>
        </div>

        <div className="appointment-actions">
          <button
            className="action-btn primary"
            onClick={handleNavigateToInbox}
          >
            View in Inbox
          </button>
          <button className="action-btn secondary" onClick={handleOpenCase}>
            Open Case
          </button>
  
        </div>
      </div>
    </div>
  );
};

export default AppointmentCard;
